import {
  Download,
  FileText,
  Image,
  Layers,
  MapPin,
  Spline,
  Square,
  Table2,
} from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";
import { Link, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { productCodeToExplorerLayerIdMappings } from "../constants";
import { viewDatasetInExplorer } from "./catalogue-page";

// Reverse mapping for NATIONAL X back to backend keys
const REVERSE_PRODUCT_MAPPING: Record<string, string> = {
  "NATIONAL PLANNING": "GEOSCAPE PLANNING",
  "NATIONAL PLANNING INSIGHTS": "GEOSCAPE PLANNING INSIGHTS",
  "NATIONAL BUILDINGS": "GEOSCAPE BUILDINGS",
  "NATIONAL CADASTRE": "GEOSCAPE CADASTRE",
  "NATIONAL PROPERTY": "GEOSCAPE PROPERTY",
  "NATIONAL TREES": "GEOSCAPE TREES",
  "NATIONAL ROADS": "GEOSCAPE ROADS",
  "NATIONAL AIRPORTS": "GEOSCAPE AIRPORTS",
  "NATIONAL RAIL": "GEOSCAPE RAIL",
  "NATIONAL LOCALITIES": "GEOSCAPE LOCALITIES",
  "NATIONAL LOCAL GOVERNMENT AREAS": "GEOSCAPE LOCAL GOVERNMENT AREAS",
  "NATIONAL WARDS": "GEOSCAPE WARDS",
  "NATIONAL SOLAR": "GEOSCAPE SOLAR",
  "NATIONAL SURFACE COVER": "GEOSCAPE SURFACE COVER",
  "NATIONAL GREENSPACE": "GEOSCAPE GREENSPACE",
  "NATIONAL HYDROLOGY": "GEOSCAPE HYDROLOGY",
  "NATIONAL ELECTORAL BOUNDARIES": "ELECTORAL BOUNDARIES",
  "NATIONAL POSTCODE BOUNDARIES": "POSTCODE BOUNDARIES",
};

interface CatalogueCardProps {
  productId: string;
  displayProduct: string;
  totalFeatureCount: number;
  shortDescription?: string;
  updatedDate?: string;
  image: string;
  dataType: string[];
}

export const CatalogueCard = (props: CatalogueCardProps) => {
  // Reverse map NATIONAL X back to backend key for backend operations
  const backendProductId =
    REVERSE_PRODUCT_MAPPING[props.productId] || props.productId;

  // Substitute PLANNING INSIGHTS for BASE version
  const datasetId =
    backendProductId === "GEOSCAPE PLANNING INSIGHTS"
      ? "GEOSCAPE PLANNING INSIGHTS - BASE"
      : backendProductId;

  const history = useHistory();

  return (
    <Card className="overflow-hidden p-0 gap-0">
      <div className="flex flex-col md:flex-row md:items-stretch flex-1">
        {/* Coverage image */}
        <div className="relative w-full md:w-60 md:min-w-40 shrink-0">
          <img
            src={props.image}
            alt={props.displayProduct}
            className="h-full w-full object-cover"
          />
          <Badge className="absolute top-2 left-2 gap-1.5">
            {getFeatureIcon(props.dataType[0])}
            {Intl.NumberFormat("en", { notation: "compact" }).format(
              props.totalFeatureCount,
            )}{" "}
            {humanizeFeatures(props.dataType[0])}
          </Badge>
        </div>

        {/* Card content */}
        <div className="flex flex-col flex-1 min-w-0 py-4 gap-4">
          <CardHeader>
            <CardTitle className="truncate">{props.displayProduct}</CardTitle>
            {props.updatedDate && (
              <CardDescription>
                Updated: {dayjs(props.updatedDate).format("MMM YYYY")}
              </CardDescription>
            )}
            <CardAction>
              <div className="flex gap-1">
                {props.dataType.map((type) => (
                  <Badge key={type} variant="outline">
                    {type}
                  </Badge>
                ))}
              </div>
            </CardAction>
          </CardHeader>

          <CardContent>
            <p className="line-clamp-3 text-sm text-muted-foreground">
              {props.shortDescription}
            </p>
          </CardContent>

          {/* Footer pinned to bottom */}
          <CardFooter className="flex gap-2 mt-auto">
            <Button className="flex-1" asChild>
              <Link to={`${Routes.dataCatalogue}/${props.productId}`}>
                <FileText />
                Details
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                history.push(`${Routes.dataDownload}?product=${datasetId}`)
              }
            >
              <Download />
              Downloads
            </Button>
            <Button
              variant="outline"
              disabled={!productCodeToExplorerLayerIdMappings.get(datasetId)}
              onClick={() => viewDatasetInExplorer(datasetId, history)}
            >
              <Layers />
              Explorer
            </Button>
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

const getFeatureIcon = (dataType: string) => {
  const className = "size-3";
  switch (dataType) {
    case "Point":
      return <MapPin className={className} />;
    case "Polygon":
    case "Multi Polygon":
      return <Square className={className} />;
    case "Line":
    case "Multi Line":
      return <Spline className={className} />;
    case "Raster":
      return <Image className={className} />;
    case "Table":
      return <Table2 className={className} />;
    default:
      return <MapPin className={className} />;
  }
};

const humanizeFeatures = (dataType: string) => {
  switch (dataType) {
    case "Raster":
      return "Sq KM";
    case "Table":
      return "Rows";
    default:
      return "Features";
  }
};
